import * as React from 'react';
import { Box, Button, Paper, Popper, Grow, Stack, MenuItem, MenuList, ClickAwayListener } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Invoice } from '../models/invoice';
import { MultiplePaymentsCreateModal } from './multiplePaymentsCreateModal';
import { CancelMultipleInvoicesDialog } from './cancelMultipleInvoicesDialog';
import { InvoiceMailSenderModal } from './invoiceMailSenderModal';

type HandleInvoicesButtonProps = {
  rows: Invoice[];
  selectionModel: GridRowSelectionModel;
  fetchData: () => void;
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
};

const HandleInvoicesButton = (props: HandleInvoicesButtonProps) => {
  const { rows, selectionModel, fetchData, setSelectionModel } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const { setDateStamp } = useGlobals();

  const [showCancelInvoiceDialog, setShowCancelInvoiceDialog] = useState(false);
  const [showMultiplePaymentsCreateModal, setShowMultiplePaymentsCreateModal] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [membersFromSelection, setMembersFromSelection] = useState<Invoice[] | undefined>(undefined);
  const [isCancelButtonDisabled, setIsCancelButtonDisabled] = useState<boolean>(false);
  const [isPaymentButtonDisabled, setIsPaymentButtonDisabled] = useState<boolean>(false);
  const [showInvoiceMailSender, setShowInvoiceMailSender] = useState<boolean>(false);

  useEffect(() => {
    const selectedIDs = new Set(selectionModel);
    const filteredRows = rows.filter((invoice) => selectedIDs.has(invoice.invoice_id));

    setMembersFromSelection((prev) => {
      if (!prev) {
        return filteredRows;
      }

      const updatedSelection = prev.filter((invoice) => selectedIDs.has(invoice.invoice_id));

      const newSelection = [
        ...updatedSelection,
        ...filteredRows.filter((invoice) => !prev.some((prevInvoice) => prevInvoice.invoice_id === invoice.invoice_id)),
      ];

      return newSelection;
    });
  }, [selectionModel, rows]);

  useEffect(() => {
    if (membersFromSelection && Array.isArray(membersFromSelection)) {
      const allCanceled = membersFromSelection.every((item) => item.status.id === InvoiceStatusEnum.Canceled);
      setIsCancelButtonDisabled(allCanceled);
    }
  }, [membersFromSelection]);

  useEffect(() => {
    if (membersFromSelection && Array.isArray(membersFromSelection)) {
      const allPaid = membersFromSelection.every(
        (item) => item.status.id === InvoiceStatusEnum.Paid || item.status.id === InvoiceStatusEnum.Canceled,
      );
      setIsPaymentButtonDisabled(allPaid);
    }
  }, [membersFromSelection]);

  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleCancelInvoice = () => {
    setOpen(false);
    setShowCancelInvoiceDialog(true);
  };

  const handleCancelInvoiceCallback = () => {
    setShowCancelInvoiceDialog(false);
  };

  const handleSendInvoiceByEmail = () => {
    setOpen(false);
    setShowInvoiceMailSender(true);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      {showCancelInvoiceDialog && membersFromSelection && (
        <CancelMultipleInvoicesDialog
          onCancel={() => {
            setSelectionModel([]);
            fetchData();
          }}
          invoiceIds={membersFromSelection.filter((invoice) => invoice.status.id !== InvoiceStatusEnum.Canceled).map((invoice) => invoice.invoice_id)}
          showDialog={showCancelInvoiceDialog}
          callback={handleCancelInvoiceCallback}
        />
      )}
      {showMultiplePaymentsCreateModal && membersFromSelection && (
        <MultiplePaymentsCreateModal
          onClose={() => setShowMultiplePaymentsCreateModal(false)}
          onSave={() => {
            setShowMultiplePaymentsCreateModal(false);
            setSelectionModel([]);
            setDateStamp(new Date());
            fetchData();
          }}
          modalOpen={showMultiplePaymentsCreateModal}
          initialValues={membersFromSelection.map((invoice) => ({
            ocr: invoice.ocr,
            amount: invoice.amount,
            comment: '',
            date: undefined,
            source: '',
            sender: '',
            memberNr: invoice.member?.memberNr,
            name: `${invoice.member?.firstName} ${invoice.member?.lastName}`,
            dueDate: invoice.dueDate,
            status: invoice.status,
          }))}
        />
      )}
      {showInvoiceMailSender && membersFromSelection && (
        <InvoiceMailSenderModal
          onClose={() => setShowInvoiceMailSender(false)}
          onSave={() => {
            setShowInvoiceMailSender(false);
            setSelectionModel([]);
          }}
          modalOpen={showInvoiceMailSender}
          invoiceIds={membersFromSelection.map((invoice) => invoice.invoice_id)}
        />
      )}

      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={() => {
              handleToggle();
            }}
            variant='contained'
            disableElevation
            endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper open={open} anchorEl={anchorRef.current} placement='bottom-start' transition disablePortal nonce={undefined} sx={{ zIndex: 999 }}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id='composition-menu' aria-labelledby='composition-button'>
                      <MenuItem onClick={() => setShowMultiplePaymentsCreateModal(true)} disabled={isPaymentButtonDisabled}>
                        {t('invoice:RegisterPayment')}
                      </MenuItem>
                      <MenuItem onClick={handleCancelInvoice} disabled={isCancelButtonDisabled}>
                        {t('invoice:Cancel')}
                      </MenuItem>
                      <MenuItem onClick={handleSendInvoiceByEmail}>{t('invoice:send:viaEmail')}</MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
    </>
  );
};

export default HandleInvoicesButton;
